// features/product/model/schema.ts
import { LANGUAGE } from '@/shared/localization';
import { z } from 'zod';
import { createPaginatedSchema } from './common';

// ==========================================
// 1. 공통 Enum & 기본 타입 정의 (Enum)
// ==========================================

export const SortBySchema = z.enum(['created_at', 'updated_at', 'name']);
export const StatusSchema = z.enum([
  'IDLE',
  'RUNNING',
  'SUCCEEDED',
  'FAILED',
  'PARTIAL',
]);
export const ProductTypeSchema = z.enum(['detail', 'video', 'banner', 'image']);

// ==========================================
// 2. 서브 스키마 정의 (Value Objects)
// ==========================================

export const ImageInfoSchema = z.object({
  classname: z.string(),
  score: z.number().optional(),
  color: z.string().optional(),
  pose: z.string().nullable().optional(),
  view: z.string().nullable().optional(),
  group: z.number().optional(),
});

export const AttachmentSchema = z.object({
  key: z.string(),
  url: z.string().url(),
  size: z.number(),
  error: z.string().nullable(),
  user_id: z.string(),
  object_id: z.string(),
  file_uuid: z.string(),
  last_modified: z.string(),
  content_type: z.string(),
  image_info: ImageInfoSchema,
  is_main_image: z.boolean().optional(),
  is_AI_generated: z.boolean().optional(),
});

// ==========================================
// 3. 메인 도메인 스키마 (Product)
// ==========================================

export const ProductSchema = z.object({
  id: z.string(),
  name: z.string(),
  category_lv0: z.string(),
  category_lv1: z.string(),
  category_lv2: z.string(),
  colors: z.array(z.string()),
  features: z.array(z.string()),
  material: z.string(),
  sizes: z.array(z.string()),
  care_instructions: z.string(),
  guide: z.string(),
  main_image: z.string().optional(), // 썸네일
  user_id: z.string(),
  created_at: z.string(),
  updated_at: z.string(),
  deleted_at: z.string().optional(),
  status: StatusSchema, // 상품 상태
  image_status: StatusSchema, // 이미지 편집 상태
  detail_status: StatusSchema, // 시안 생성 상태
  video_status: StatusSchema, // 비디오 생성 상태
  banner_status: StatusSchema, // 배너 생성 상태
  notice_maker: z.string().optional(),
  notice_origin: z.string().optional(),
  notice_manufacture_date: z.string().optional(),
  notice_warranty: z.string().optional(),
  notice_as: z.string().optional(),
  extra: z.record(z.string(), z.unknown()), // Record<string, unknown>
});

// ==========================================
// 4. Request 스키마
// ==========================================

// 상품 조회 요청
export const ProductRequestParamsSchema = z.object({
  page: z.number().optional(),
  pagesize: z.number().optional(),
  search: z.string().nullable().optional(),
  sort_by: SortBySchema.optional(),
  ids: z.array(z.string()).optional(),
});

// ProductSchema에서 업데이트 가능한 필드만 선택하여 자동 생성
export const UpdateProductRequestSchema = ProductSchema.pick({
  name: true,
  category_lv1: true,
  category_lv2: true,
  main_image: true,
  sizes: true,
  features: true,
  material: true,
  colors: true,
  notice_maker: true,
  notice_origin: true,
  notice_manufacture_date: true,
  notice_warranty: true,
  notice_as: true,
}).partial();

// 썸네일 지정하기
export const UpdateProductMainImageRequestSchema = z.object({
  main_image_uuid: z.string(),
});

export const CreateProductV2RequestSchema = z.object({
  product_type: ProductTypeSchema,
  name: z.string(),
  brand: z.string().optional(),
  lang: z.nativeEnum(LANGUAGE),
  category_lv0: z.string(),
  category_lv1: z.string(), // 없으면 category_lv0
  category_lv2: z.string(), // 없으면 category_lv0
  gender: z.string().optional(),
  age_group: z.string().optional(),
  detail_options: z
    .object({
      use_ai_generated: z.boolean(),
      uploaded_buf_keys: z.array(z.string()),
    })
    .optional(),
  video_options: z
    .object({
      ratio: z.string(),
      resolution: z.string(),
      length: z.number(),
      style: z.string(),
      source_images: z.array(
        z.object({
          key: z.string(),
          idx: z.number(),
        }),
      ),
    })
    .optional(),
  banner_options: z
    .object({
      aspect_ratio: z.string(),
      main_text: z.string().optional(),
      sub_text: z.string().optional(),
      use_ai_generated: z.boolean().optional(),
      auto_generate_main_image: z.boolean(),
      main_image_buf_keys: z.array(z.string()),
      stickers: z
        .array(
          z.object({
            slot: z.number(),
            type: z.enum(['my_sticker', 'custom_sticker']),
            text: z.string().optional(),
            buf_key: z.string().optional(),
          }),
        )
        .optional(),
      template_type: z.string(),
      background_type: z.string().optional(),
    })
    .optional(),
  ai_studio_options: z
    .object({
      uploaded_buf_keys: z.array(z.string()),
    })
    .optional(),
});

// 이미지 추가 요청
export const AddProductAttachmentV2RequestSchema = z.object({
  uploaded_buf_keys: z.array(z.string()),
});

// ==========================================
// 5. Response 스키마
// ==========================================

export const ListProductResponseSchema = createPaginatedSchema(ProductSchema);

export const ReadProductResponseSchema = z.object({
  product: ProductSchema,
  attachments: z.array(AttachmentSchema),
});

export const CreateProductResponseSchema = ProductSchema;
export const DeleteProductAttachmentResponseSchema = z.object({
  deleted_ids: z.array(z.string()),
});

// presigned url
export const PostPresignedUrlRequestSchema = z.array(
  z.object({
    presigned_url: z.string(),
    key: z.string(),
  }),
);
// ==========================================
// 6. 타입 추출
// ==========================================

export type SortBy = z.infer<typeof SortBySchema>;
export type Status = z.infer<typeof StatusSchema>;
export type ProductType = z.infer<typeof ProductTypeSchema>;
export type ImageInfo = z.infer<typeof ImageInfoSchema>;

export type ProductDto = z.infer<typeof ProductSchema>;
export type AttachmentDto = z.infer<typeof AttachmentSchema>;

export type ProductRequestParams = z.infer<typeof ProductRequestParamsSchema>;

export type UpdateProductRequest = z.infer<typeof UpdateProductRequestSchema>;
export type UpdateProductMainImageRequest = z.infer<
  typeof UpdateProductMainImageRequestSchema
>;
export type UpdateProductImageInfoRequest = z.infer<typeof ImageInfoSchema>;

// V2
export type CreateProductV2Request = z.infer<
  typeof CreateProductV2RequestSchema
>;
export type AddProductAttachmentV2Request = z.infer<
  typeof AddProductAttachmentV2RequestSchema
>;

export type ListProductResponse = z.infer<typeof ListProductResponseSchema>;
export type ReadProductResponse = z.infer<typeof ReadProductResponseSchema>;
export type CreateProductResponse = z.infer<typeof CreateProductResponseSchema>;
export type UpdateProductResponse = z.infer<typeof ProductSchema>;
export type DeleteProductAttachmentResponse = z.infer<
  typeof DeleteProductAttachmentResponseSchema
>;
export type PostPresignedUrlRequest = z.infer<
  typeof PostPresignedUrlRequestSchema
>;
